import axios from 'axios'
import CreateReducer from '../utils/createReducer'
import CreateActions from '../utils/createActions'

import api from '../api'

import networkWrapper from '../utils/networkWrapper'
import snackbarActions from './snackbar'
import paymentSelectors from '../selectors/payment'

const prefix = 'payment'

const initialState = {
  loaded: false,
  products: [],
  customer: null,
  team: null,
  card: null,
  invoices: [],
  cancelledSubscriptions: [],
  cancelledSubscriptionInvoices: [],
  // this is the proation info from stripe
  // that we use to display the change in billing
  // for the rest of the month for the upgrade
  changePlanInfo: null,
}

const reducers = {
  setLoaded: (state, action) => {
    state.loaded = action.payload
  },
  setProducts: (state, action) => {
    state.products = action.payload
  },
  setCustomer: (state, action) => {
    state.customer = action.payload
  },
  setTeam: (state, action) => {
    state.team = action.payload
  },
  setCard: (state, action) => {
    state.card = action.payload
  },
  setInvoices: (state, action) => {
    state.invoices = action.payload
  },
  setCancelledSubscriptions: (state, action) => {
    state.cancelledSubscriptions = action.payload
  },
  setCancelledSubscriptionInvoices: (state, action) => {
    state.cancelledSubscriptionInvoices = action.payload
  },
  setChangePlanInfo: (state, action) => {
    state.changePlanInfo = action.payload
  },
}


const loaders = {

  loadProducts: () => axios.get(api.url('/payment/products'))
    .then(api.process),

  loadStatus: () => axios.get(api.url('/payment/status'))
    .then(api.process),

  changePlan: (payload) => axios.post(api.url('/payment/plan'), payload)
    .then(api.process),

  cancelSubscription: () => axios.post(api.url(`/payment/subscription/cancel`))
    .then(api.process),

  loadCancelledSubscriptionInvoices: (subscriptionId) => axios
    .get(api.url(`/payment/subscription/${subscriptionId}/invoices`))
    .then(api.process),

  changeCard: (payload) => axios.post(api.url(`/payment/card`), payload)
    .then(api.process),

}

const sideEffects = {

  loadProducts: () => networkWrapper({
    prefix,
    name: 'loadProducts',
    handler: async (dispatch, getState) => {
      const data = await loaders.loadProducts()
      dispatch(actions.setProducts(data))
    },
    errorHandler: (dispatch, getState, errorMessage) => {
      dispatch(snackbarActions.setError(`there was an error listing the products: ${errorMessage}`))
    }
  }),
  
  loadStatus: () => networkWrapper({
    prefix,
    name: 'loadStatus',
    handler: async (dispatch, getState) => {
      const {
        customer,
        team,
        card,
        invoices,
        cancelledSubscriptions,
      } = await loaders.loadStatus()
      
      dispatch(actions.setCustomer(customer))
      dispatch(actions.setTeam(team))
      dispatch(actions.setCard(card))
      dispatch(actions.setInvoices(invoices))
      dispatch(actions.setCancelledSubscriptions(cancelledSubscriptions))
      dispatch(actions.setLoaded(true))
    },
    errorHandler: (dispatch, getState, errorMessage) => {
      dispatch(snackbarActions.setError(`there was an error listing the payment status: ${errorMessage}`))
    }
  }),

  changePlan: (product) => networkWrapper({
    prefix,
    name: 'changePlan',
    globalLoading: true,
    handler: async (dispatch, getState) => {
      const page_url = window.location.href
      const result = await loaders.changePlan({
        product: product.id,
        page_url,
      })
      if(result.action == 'checkout') {
        if(!result.stripe_public_key) throw new Error(`no stripe public key found`)
        if(!result.session_id) throw new Error(`no session_id key found`)
        const stripe = Stripe(result.stripe_public_key)
        const {error} = await stripe.redirectToCheckout({
          sessionId: result.session_id,
        })
        if(error) throw new Error(error)
      }
      else if(result.action == 'change_plan_confirmation') {
        dispatch(actions.setChangePlanInfo(result))
      }
      else {
        throw new Error(`unknown changeSharedPlan action: ${result.action}`)
      }
    },
    errorHandler: (dispatch, getState, errorMessage) => {
      dispatch(snackbarActions.setError(`there was an error changing the plan: ${errorMessage}`))
    }
  }),

  confirmChangePlan: () => networkWrapper({
    prefix,
    name: 'confirmChangePlan',
    globalLoading: true,
    handler: async (dispatch, getState) => {
      const changePlanInfo = paymentSelectors.changePlanInfo(getState())
      if(!changePlanInfo) throw new Error(`no change of plan found`)
      if(!changePlanInfo.prorationDate) throw new Error(`no prorationDate of change plan found`)
      const page_url = window.location.href
      if(!changePlanInfo.productId) throw new Error(`no product id found in change plan info`)
      const result = await loaders.changePlan({
        product: changePlanInfo.productId,
        prorationDate: changePlanInfo.prorationDate,
        page_url,
      })
      if(result.action != 'update_confirmed') {
        throw new Error(`got error response from change plan ${result.action}`)
      }
      await dispatch(actions.loadStatus())
      dispatch(actions.setChangePlanInfo(null))
      dispatch(snackbarActions.setSuccess(`your plan has been updated`))
      return true
    },
    errorHandler: (dispatch, getState, errorMessage) => {
      dispatch(snackbarActions.setError(`there was an error changing the plan: ${errorMessage}`))
    }
  }),

  cancelSubscription: () => networkWrapper({
    prefix,
    name: 'cancelSubscription',
    globalLoading: true,
    handler: async (dispatch, getState) => {
      await loaders.cancelSubscription()
      await dispatch(actions.loadStatus())
      dispatch(snackbarActions.setSuccess(`Your subscription has been cancelled`))
      return true
    },
    errorHandler: (dispatch, getState, errorMessage) => {
      dispatch(snackbarActions.setError(`there was an error cancelling the subscription: ${errorMessage}`))
    }
  }),

  loadCancelledSubscriptionInvoices: (subscriptionId) => networkWrapper({
    prefix,
    name: 'loadCancelledSubscriptionInvoices',
    handler: async (dispatch, getState) => {
      dispatch(actions.setCancelledSubscriptionInvoices([]))
      const result = await loaders.loadCancelledSubscriptionInvoices(subscriptionId)
      dispatch(actions.setCancelledSubscriptionInvoices(result))
    },
    errorHandler: (dispatch, getState, errorMessage) => {
      dispatch(snackbarActions.setError(`there was an error loading invoices: ${errorMessage}`))
    }
  }),

  changeCard: () => networkWrapper({
    prefix,
    name: 'changeCard',
    handler: async (dispatch, getState) => {
      const page_url = document.location.href
      const result = await loaders.changeCard({page_url})
      if(result.action == 'checkout') {
        if(!result.stripe_public_key) throw new Error(`no stripe public key found`)
        if(!result.session_id) throw new Error(`no session_id key found`)
        const stripe = Stripe(result.stripe_public_key)
        const {error} = await stripe.redirectToCheckout({
          sessionId: result.session_id,
        })
        if(error) throw new Error(error)
      }
      else {
        throw new Error(`unknown changeCard action: ${result.action}`)
      }
    },
    errorHandler: (dispatch, getState, errorMessage) => {
      dispatch(snackbarActions.setError(`there was an error changing card: ${errorMessage}`))
    }
  }),

}

const reducer = CreateReducer({
  initialState,
  reducers,
  prefix,
})

const actions = CreateActions({
  reducers,
  sideEffects,
  prefix,
})

export { actions, reducer }
export default actions
